import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  unknownLocationService,
  type UnknownLocationReport,
  type ResolveReportRequest,
  type BulkResolveResult,
} from '../../services/unknownLocationService';
import { scottishSchoolService } from '../../services/scottishSchoolService';

type ResolutionPath = 'LINKED_EXISTING' | 'ADDED_AND_LINKED' | 'CORRECTED_COUNCIL';

interface ResolveState {
  report: UnknownLocationReport | null;
  path: ResolutionPath;
  schoolList: { id: number; name: string }[];
  selectedSchoolId: string;
  selectedSchoolName: string;
  newSchoolName: string;
  bulkResult: BulkResolveResult | null;
  saving: boolean;
  error: string | null;
}

const EMPTY_RESOLVE: ResolveState = {
  report: null,
  path: 'LINKED_EXISTING',
  schoolList: [],
  selectedSchoolId: '',
  selectedSchoolName: '',
  newSchoolName: '',
  bulkResult: null,
  saving: false,
  error: null,
};

const UnknownLocationReports: React.FC = () => {
  const [reports, setReports] = useState<UnknownLocationReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [typeFilter, setTypeFilter] = useState('');

  const [resolve, setResolve] = useState<ResolveState>(EMPTY_RESOLVE);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await unknownLocationService.getReports(
        statusFilter || undefined,
        typeFilter || undefined,
        page,
        rowsPerPage
      );
      setReports(result.content);
      setTotal(result.totalElements);
    } catch {
      // keep existing list
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, page, rowsPerPage]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const openResolve = async (report: UnknownLocationReport) => {
    const schoolList = await unknownLocationService.getSchoolsForResolution(
      report.councilId, report.reportType === 'UNKNOWN_SCHOOL' ? report.schoolType : report.schoolType
    );
    setResolve({ ...EMPTY_RESOLVE, report, schoolList });
  };

  const loadSchools = async (councilId: number | null, schoolType: string) => {
    const list = await unknownLocationService.getSchoolsForResolution(councilId, schoolType);
    setResolve(prev => ({ ...prev, schoolList: list }));
  };

  const handlePathChange = async (path: ResolutionPath) => {
    if (!resolve.report) return;
    setResolve(prev => ({ ...prev, path, selectedSchoolId: '', selectedSchoolName: '' }));
    if (path === 'CORRECTED_COUNCIL') {
      // Cross-council search — load all schools of same type
      const list = await scottishSchoolService.getAllSchools(resolve.report.schoolType);
      setResolve(prev => ({ ...prev, schoolList: list.map(s => ({ id: s.id, name: `${s.name} (${s.councilName})` })) }));
    } else if (resolve.report.councilId) {
      await loadSchools(resolve.report.councilId, resolve.report.schoolType);
    }
  };

  const handleConfirmResolve = async (applyToDuplicates = false) => {
    if (!resolve.report) return;
    setResolve(prev => ({ ...prev, saving: true, error: null }));
    try {
      const req: ResolveReportRequest = {
        resolutionType: resolve.path,
        resolvedSchoolId: resolve.selectedSchoolId ? Number(resolve.selectedSchoolId) : null,
        resolvedSchoolName: resolve.path === 'ADDED_AND_LINKED' ? resolve.newSchoolName : resolve.selectedSchoolName,
        applyToDuplicates,
      };
      const result = await unknownLocationService.resolveReport(resolve.report.id, req);
      setResolve(prev => ({ ...prev, bulkResult: result, saving: false }));
      if (applyToDuplicates || result.matchingPendingCount === 0) {
        setResolve(EMPTY_RESOLVE);
        loadReports();
      }
    } catch (e: unknown) {
      setResolve(prev => ({ ...prev, saving: false, error: e instanceof Error ? e.message : 'Failed to resolve' }));
    }
  };

  const canConfirm = resolve.path === 'ADDED_AND_LINKED'
    ? !!resolve.newSchoolName.trim()
    : !!resolve.selectedSchoolId;

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="RESOLVED">Resolved</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Report type</InputLabel>
          <Select value={typeFilter} label="Report type" onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="UNKNOWN_SCHOOL">Unknown school</MenuItem>
            <MenuItem value="UNKNOWN_COUNCIL">Unknown council</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Child</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Council</TableCell>
                <TableCell>School type</TableCell>
                <TableCell>Reported school</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.childName}</TableCell>
                  <TableCell>
                    <Chip label={r.reportType === 'UNKNOWN_SCHOOL' ? 'Unknown school' : 'Unknown council'} size="small" />
                    {r.isDuplicateChild && (
                      <Chip label="Duplicate — same child" color="error" size="small" sx={{ ml: 0.5 }} />
                    )}
                    {r.isDuplicate && !r.isDuplicateChild && (
                      <Chip label="Cross-child match" color="warning" size="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>{r.councilName}</TableCell>
                  <TableCell>{r.schoolType}</TableCell>
                  <TableCell>{r.reportedSchoolName}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.status}
                      color={r.status === 'PENDING' ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {r.status === 'PENDING' && (
                      <Button size="small" variant="outlined" onClick={() => openResolve(r)}>
                        Resolve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {reports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No reports found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20]}
            onPageChange={(_, p) => setPage(p)}
          />
        </>
      )}

      {/* Resolve modal */}
      <Dialog open={!!resolve.report} onClose={() => setResolve(EMPTY_RESOLVE)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve report — {resolve.report?.reportedSchoolName}</DialogTitle>
        <DialogContent>
          {resolve.bulkResult && resolve.bulkResult.matchingPendingCount > 0 ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {resolve.bulkResult.matchingPendingCount} other pending report(s) match this school name. Apply the same resolution to all?
              </Typography>
              {resolve.error && <Typography color="error" variant="body2">{resolve.error}</Typography>}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {resolve.error && <Typography color="error" variant="body2">{resolve.error}</Typography>}

              <RadioGroup value={resolve.path} onChange={e => handlePathChange(e.target.value as ResolutionPath)}>
                <FormControlLabel value="LINKED_EXISTING" control={<Radio />} label="Path A — Link to existing school" />
                <FormControlLabel value="ADDED_AND_LINKED" control={<Radio />} label="Path B — Add new school then link" />
                <FormControlLabel value="CORRECTED_COUNCIL" control={<Radio />} label="Path C — Correct council then pick school" />
              </RadioGroup>

              <Divider />

              {/* Path A and C: school dropdown */}
              {(resolve.path === 'LINKED_EXISTING' || resolve.path === 'CORRECTED_COUNCIL') && (
                <FormControl fullWidth>
                  <InputLabel>School</InputLabel>
                  <Select
                    value={resolve.selectedSchoolId}
                    label="School"
                    onChange={e => {
                      const id = e.target.value;
                      const name = resolve.schoolList.find(s => String(s.id) === id)?.name ?? '';
                      setResolve(prev => ({ ...prev, selectedSchoolId: id, selectedSchoolName: name }));
                    }}
                  >
                    {resolve.schoolList.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Path B: new school name */}
              {resolve.path === 'ADDED_AND_LINKED' && (
                <TextField
                  fullWidth
                  label="New school name"
                  value={resolve.newSchoolName}
                  onChange={e => setResolve(prev => ({ ...prev, newSchoolName: e.target.value }))}
                  helperText="Add the school to the Scottish curriculum service then link this report"
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResolve(EMPTY_RESOLVE); loadReports(); }}>
            {resolve.bulkResult && resolve.bulkResult.matchingPendingCount > 0 ? 'Skip' : 'Cancel'}
          </Button>
          {resolve.bulkResult && resolve.bulkResult.matchingPendingCount > 0 ? (
            <Button variant="contained" onClick={() => handleConfirmResolve(true)} disabled={resolve.saving}>
              {resolve.saving ? 'Applying…' : 'Apply to all'}
            </Button>
          ) : (
            <Button variant="contained" onClick={() => handleConfirmResolve(false)} disabled={resolve.saving || !canConfirm}>
              {resolve.saving ? 'Saving…' : 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnknownLocationReports;
